// R116 — **THE MAGIC ROOM'S ONE OVERLAY, AND C2's COST-PROTECTION LAW WAS OFF IN IT.**
//
// MR-B4 / MR-O3: every working opens as a popup OVER the still-visible room (no separate page, no
// Back button) — the scene stays behind and the working floats in the site's shared black/gold glass
// panel. This is the single glass panel every Magic Room generator wears (Petition, Keys, Oils,
// Moses, Herbs, Notepaper, the Cauldron), so their panels can never drift apart again.
//
// **WHAT IT WAS DOING, AND SEVEN GENERATORS DELIVER A PAID WORKING THROUGH IT.** C2 is written out in
// full one file over, in `DeliveredReading.tsx`, and it is a COST law rather than a taste one:
//
//   "COST-PROTECTION LAW (C2): a generated result is never dismissed by accident. Click-outside /
//    background does NOTHING here (deliberately the reverse of the informational-popup rule). It
//    closes ONLY via its X or Escape, and if it has not been saved, that close first asks to confirm
//    ... so a member never loses a reading they paid to generate."
//
// `ModalBackdrop` — the shell every OTHER generator popup on this site is drawn in — obeys it
// STRUCTURALLY: there is no click handler on the scrim to obey it with. This shell, 3,000 lines away
// in App.tsx, carried `onMouseDown={onClose}` on the backdrop and its own prose stating the opposite
// ("the scrim, the X, or Escape dismisses it"), written before C2 existed. So the ONE room where
// every single popup is a paid working is the one room a stray press beside the panel threw it away
// in — and it took the half-typed petition, the picked oils and the stacked Cauldron with it, because
// dismissing the shell unmounts the generator whole.
//
// **THE SCRIM IS GONE AND NOTHING REPLACES IT.** The X and Escape were always the ways out and both
// still are. This is C2's own sentence, applied to the shell that had never been held to it.
//
// **AND THE SECOND HALF WAS OV2-9, WHICH LIVES ON `ModalBackdrop` AND WAS NOT HERE.** A bottom-bar tab
// goes where it says and takes whatever is up with it. `ClosableReport` inside this shell DOES hear
// that announcement, so a tab used to take the delivered reading away and leave the empty generator
// panel standing over the room — the nav half-obeyed, which reads as the house being stuck rather
// than as a rule. The listener is on the shell now, so the whole working goes at once.
//
// **THE ESCAPE HANDLER IS DELIBERATELY NOT `useEscapeKey` AND IS DECLARED IN `r82-escape-law.mjs`
// WITH ITS REASON.** It listens on `document` and stops propagation on purpose: `document` is earlier
// in the bubble path than `window`, so a nested `ModalBackdrop` inside a working cannot close the
// room behind it. What that never did — and R116 is the round that measured it — is stop a listener
// on `document` ITSELF, which is where `ClosableReport` listens; that half is fixed on the reading's
// side, where the reading can claim the keypress it is answering.
//
// It moved out of App.tsx as this round's line payment, and in this line's favourite currency: the
// thing that had to move is the thing nothing could reach. Out here `scripts/r116-overlay-shells.mjs`
// CALLS it and reads back the tree it returns and the listeners it registered.
import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { CLOSE_OVERLAYS_EVENT } from "./MobileNav";

export function MagicOverlay({ label, onClose, children }: { label: string; onClose: () => void; children: ReactNode }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // OV2-9's half, the same effect `ModalBackdrop` carries: a tab takes the whole working with it.
  useEffect(() => {
    const step = () => onClose();
    window.addEventListener(CLOSE_OVERLAYS_EVENT, step);
    return () => window.removeEventListener(CLOSE_OVERLAYS_EVENT, step);
  }, [onClose]);
  if (typeof document === "undefined") return null;
  return createPortal(
    // C2: NO handler on the scrim. A generated working is not dismissed by a stray press.
    <div className="modal-backdrop mr-overlay-backdrop" role="dialog" aria-modal="true" aria-label={label}>
      <div className="mr-overlay-panel">
        <button className="mr-overlay-panel__close" type="button" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
        {children}
      </div>
    </div>,
    document.body,
  );
}
